/* eslint-disable react-compiler/react-compiler */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { RestockingSource } from "__graphql__/types";
import { PageName } from "analytics/events";
import { EppoFeatureFlags } from "app/types/flags";
import { config } from "config";
import { usePublicRestockingList } from "flows/Inventory/flows/RestockingList/services/restockingService/service";
import { Campaign } from "flows/Picking/components/Campaign";
import { DuplicateWeightModal } from "flows/Picking/components/DuplicateWeightModal";
import { RemoveFlyers } from "flows/Picking/components/RemoveFlyers";
import ScanJETReweBagModal from "flows/Picking/components/ScanJETReweBagModal";
import { Page } from "shared/components/Page";
import { JET_REWE_RESELLERS, WOLT_RESELLERS } from "shared/constants";
import { ErrorTypeForAnalytics, useAnalytics } from "shared/hooks/useAnalytics";
import { useBarcodeScanner } from "shared/hooks/useBarcodeScanner";
import { useCountryCode } from "shared/hooks/useCountryCode";
import { useCustomToast, VARIANT_INFO_SUCCESS } from "shared/hooks/useCustomToast";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { useErrorToastWithAudio } from "shared/hooks/useErrorToastWithAudio";
import { useScan } from "shared/hooks/useScan/useScan";
import { BOTTOM_TAB_BAR_HEIGHT_NUMBER } from "ui/BottomTabBarContainer";
import { Button } from "ui/Button/Button";
import { WarningRoundFilledIcon } from "ui/Icons/Icons";
import { SeparatorHeader } from "ui/SeparatorHeader/SeparatorHeader";
import { BodyM, TitleM } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { EndPickingButton } from "../../components/EndPickingButton";
import { Gift } from "../../components/Gift";
import { GoToScanContainersButton } from "../../components/GoToScanContainersButton";
import { ItemNotFoundModal } from "../../components/ItemNotFoundModal";
import { OrderCompletionWarningModal } from "../../components/OrderCompletionWarningModal";
import { PickingConflictModal } from "../../components/PickingConflictModal";
import { PickingListHeader } from "../../components/PickingListHeader";
import { PickingListItem } from "../../components/PickingListItem/PickingListItem";
import { usePickingActions } from "../../hooks/usePickingActions";
import { usePickingListScrollEffects } from "../../hooks/usePickingListScrollEffects";
import {
  useSelectItemsSortedByShelf,
  useSelectCurrentItem as useSelectCurrentItemToPick,
} from "../../hooks/usePickingMachineSelectors";
import { usePickingUIStore } from "../../hooks/usePickingUIStore";
import { Item } from "../../models/item/types";
import { usePickingStore } from "../../stores/usePickingStore";
import { useVariablePriceStore } from "../../stores/useVariablePriceStore";
import {
  WeightBasedPricingEntry,
  findDuplicateWeight,
  parseVariablePriceBarcode,
} from "../../utils/variablePriceBarcode";

const BARCODE_ISSUE_REPORTED_TOAST_ID = "barcode_issue_reported_toast_id";
const ASK_FOR_RESTOCKING_ERROR_TOAST_ID = "ask_for_restocking_error_toast_for_sku";

export function PickingListPage() {
  const intl = useIntl();
  const { showToastUI, closeToast } = useCustomToast();
  const { processBarcode } = useBarcodeScanner();

  const { addItemToPublicRestockingList, restockingItems } = usePublicRestockingList();
  const [selectedItemsForRestocking, setSelectedItemsForRestocking] = useState<string[]>(
    restockingItems ? restockingItems.map((item) => item.sku) : [],
  );

  const { isFeatureEnabled: isCampaignProductFlagEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.CAMPAIGN_PRODUCT,
  );
  const { isFeatureEnabled: isWelcomeFlyerFlagEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.WELCOME_FLYER,
  );
  const { showToastAndPlayError, closeIfToastActive } = useErrorToastWithAudio({
    errorToastId: "scan_error_toast",
  });

  const { sendSegmentTrackEvent } = useAnalytics();

  const {
    finishPicking,
    resetPicking,
    confirmOrderCompletion,
    incrementItemQuantity,
    decrementItemQuantity,
    skipItem,
    resetItem,
  } = usePickingActions();

  const { endPickingConflictData, shouldShowOrderCompletionWarning } = usePickingUIStore(
    useShallow((state) => ({
      endPickingConflictData: state.endPickingConflictData,
      shouldShowOrderCompletionWarning: state.shouldShowOrderCompletionWarning,
    })),
  );

  // Reset the transient modal state on mount so a stale value from a previous
  // order doesn't immediately re-open the modal.
  useEffect(() => {
    usePickingUIStore.getState().reset();
  }, []);

  // Start the elapsed time timer when entering the picking list
  useEffect(() => {
    usePickingStore.getState().startTimer();
    return () => {
      usePickingStore.getState().stopTimer();
    };
  }, []);

  const currentOrder = usePickingStore((state) => state.order);

  // We have discovered a bug in the app that if sometimes OAs press native back button instead of
  // clicking the picking done button(labeled Next Order) and some orders stuck in picking state.
  // So we are adding this check to finish picking.
  // Run on mount only — reacting to state changes also catches the legitimate
  // end-picking flow for external delivery orders (where EndPickingButton lives
  // on this page) and races with the navigation to PickingDone.
  const isEndPickingIdle = usePickingStore((state) => state.endPickingPhase === "idle");
  const isPickingFinished = usePickingStore((state) => state.pickingPhase === "finished");
  useEffect(() => {
    if (!currentOrder || (isEndPickingIdle && isPickingFinished)) {
      finishPicking();
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const reseller = currentOrder?.reseller ?? "";
  const isExternalProvider = [
    "UBER-EATS",
    "UBER-EATS-CARREFOUR",
    ...WOLT_RESELLERS,
    "JUST-EAT",
    ...JET_REWE_RESELLERS,
  ].includes(reseller);
  const isNewCustomer = reseller === "FLINK" && currentOrder?.isNewCustomer === true;

  const {
    itemsSortedByShelf,
    skippedItemsSortedByShelf,
    isPickingDone,
    isGiftPicked,
    isCampaignPicked,
    hasFlyersRemoved,
  } = useSelectItemsSortedByShelf();
  const isGiftItemPicked = isWelcomeFlyerFlagEnabled ? isGiftPicked : true;
  const isCampaignItemPicked = isCampaignProductFlagEnabled ? isCampaignPicked : true;

  const currentItemToPick = useSelectCurrentItemToPick();

  const currentItem = currentItemToPick?.item;

  const countryCode = useCountryCode();

  const {
    itemsRef,
    skippedItemsHeaderRef,
    skippedItemsRef,
    endPickingButtonRef,
    giftsHeaderRef,
    removeFlyersHeaderRef,
  } = usePickingListScrollEffects();

  const {
    isOpen: isItemNotFoundModalOpen,
    onOpen: onOpenItemNotFoundModal,
    onClose: onCloseItemNotFoundModal,
  } = useDisclosure();

  const {
    isOpen: isScanJETReweBagModalOpen,
    onOpen: onOpenScanJETReweBagModal,
    onClose: onCloseScanJETReweBagModal,
  } = useDisclosure();

  const {
    isOpen: isDuplicateWeightModalOpen,
    onOpen: onOpenDuplicateWeightModal,
    onClose: onCloseDuplicateWeightModal,
  } = useDisclosure();

  const pendingWeightEntryRef = useRef<{
    itemId: string;
    entry: WeightBasedPricingEntry;
  } | null>(null);

  // Tracks how many times each price (per item) has been flagged as duplicate.
  // Key: `${itemId}:${priceInCents}` → count of times the warning was shown.
  const duplicateWeightAttemptsRef = useRef<Record<string, number>>({});
  const [allowAddDuplicateWeight, setAllowAddDuplicateWeight] = useState(false);

  const handleOnCloseScanJETReweBagModal = useCallback(
    (method: string) => {
      onCloseScanJETReweBagModal();
      if (currentOrder?.id && currentOrder?.number) {
        sendSegmentTrackEvent("orderProgressed", {
          action: "bag_picked",
          product_sku: null,
          ean: null,
          order_id: currentOrder.id,
          order_number: currentOrder.number,
          quantity: 1,
          method,
          reason: null,
          scanned_barcode: null,
          product_pricing_type: null,
          alert_shown: false,
          alert_issue_type: null,
        });
      }
    },
    [currentOrder, onCloseScanJETReweBagModal, sendSegmentTrackEvent],
  );

  useEffect(() => {
    if (JET_REWE_RESELLERS.includes(reseller)) {
      onOpenScanJETReweBagModal();
    }
  }, [onOpenScanJETReweBagModal, reseller]);

  const incrementCurrentItemQuantity = useCallback(
    ({
      reportedBarcodeIssue = false,
      pickedByScan = false,
      scannedBarcode,
    }: {
      reportedBarcodeIssue?: boolean;
      pickedByScan?: boolean;
      scannedBarcode?: string;
    }) => {
      if (!currentItemToPick) {
        return;
      }
      incrementItemQuantity(
        currentItemToPick.item.id,
        currentItemToPick.isSkipped,
        reportedBarcodeIssue,
        pickedByScan,
        scannedBarcode,
      );
    },
    [currentItemToPick, incrementItemQuantity],
  );

  const decrementCurrentItemQuantity = useCallback(
    (itemId: string) => {
      decrementItemQuantity(itemId);
    },
    [decrementItemQuantity],
  );

  const handleUpdateItemQuantity = useCallback(
    (itemId: string, decrement: boolean = false) => {
      if (decrement) {
        decrementCurrentItemQuantity(itemId);
      } else if (itemId === currentItemToPick?.item.id) {
        incrementCurrentItemQuantity({});
      }
    },
    [currentItemToPick],
  );

  const skipCurrentItem = useCallback(
    (callback?: () => void) => () => {
      if (!currentItemToPick) {
        return;
      }
      skipItem(currentItemToPick.item.id, currentItemToPick?.isSkipped);
      if (callback) {
        callback();
      }
    },
    [currentItemToPick, skipItem],
  );

  const scanLockedRef = useRef<string | null>(null);

  const onConfirmBarcodeIssue = useCallback(() => {
    scanLockedRef.current = null;
    closeIfToastActive();
    incrementCurrentItemQuantity({ reportedBarcodeIssue: true });
    showToastUI({
      id: BARCODE_ISSUE_REPORTED_TOAST_ID,
      duration: 2000,
      variant: VARIANT_INFO_SUCCESS,
      title: "pages.picking.picking-list.barcode_issue_reported_toast.title",
      containerStyle: { marginBottom: BOTTOM_TAB_BAR_HEIGHT_NUMBER },
    });
  }, [incrementCurrentItemQuantity, showToastUI]);

  // "parse_failed": barcode wasn't a valid format (likely QR / shelf label / wrong EAN).
  // "wrong_product": parsed cleanly but wbp_id / EAN doesn't match this item.
  const renderBarcodeErrorToast = useCallback(
    (variant: "parse_failed" | "wrong_product") => {
      const isParseFail = variant === "parse_failed";
      const color = isParseFail ? "red.500" : "orange.500";
      const titleKey = isParseFail
        ? "pages.picking.picking-list.invalid-barcode-toast"
        : "pages.picking.picking-list.wrong-barcode-toast";
      const bodyKey = isParseFail
        ? "pages.picking.picking-list.invalid-barcode-toast.body"
        : "pages.picking.picking-list.wrong-barcode-toast.body";
      return (
        <Flex
          alignItems="center"
          borderRadius="sm"
          h="5.5rem"
          px="s200"
          py="s100"
          gap="s200"
          bgColor={color}
        >
          <WarningRoundFilledIcon color="white" boxSize="1.5rem" />
          <Flex flexDirection="column">
            <TitleM color="white">{intl.formatMessage({ id: titleKey })}</TitleM>
            <BodyM color="white">{intl.formatMessage({ id: bodyKey })}</BodyM>
          </Flex>
          <Box width="1px" height="1.7rem" bg="white" />
          <Button bg={color} _hover={{ bg: color }} onClick={onConfirmBarcodeIssue}>
            <TitleM color="white">
              {intl.formatMessage({ id: "pages.picking.picking-list.wrong-barcode-toast.cta" })}
            </TitleM>
          </Button>
        </Flex>
      );
    },
    [intl, onConfirmBarcodeIssue],
  );

  const trackScanProductFailed = useCallback(
    (barcode: string) => {
      sendSegmentTrackEvent("errorShown", {
        screen_name: PageName.PICKING_PAGE,
        component_value: ErrorTypeForAnalytics.SCAN_PRODUCT_FAILED,
        component_name: barcode,
        component_content: currentItem?.sku ?? undefined,
        component_variant: currentOrder?.number ?? undefined,
      });
    },
    [currentItem?.sku, currentOrder?.number, sendSegmentTrackEvent],
  );

  // Show a scan-error toast + fire analytics. Skips if the same barcode was just
  // shown (prevents DataWedge double-fire spam and repeated-trigger noise).
  const showScanError = useCallback(
    (variant: "parse_failed" | "wrong_product", barcode: string) => {
      if (scanLockedRef.current === barcode) return;
      scanLockedRef.current = barcode;
      showToastAndPlayError({
        duration: 10000,
        onCloseComplete: () => {
          scanLockedRef.current = null;
        },
        replaceIfActive: true,
        render: () => renderBarcodeErrorToast(variant),
      });
      trackScanProductFailed(barcode);
    },
    [showToastAndPlayError, renderBarcodeErrorToast, trackScanProductFailed],
  );

  const isCurrentItemVariablePrice =
    currentItemToPick?.item.hasVariablePriceUnit &&
    (currentItemToPick?.item.variablePriceCodes.length ?? 0) > 0;

  const onScan = useCallback(
    (barcode: string) => {
      closeToast(BARCODE_ISSUE_REPORTED_TOAST_ID);
      if (isScanJETReweBagModalOpen) return;

      if (isDuplicateWeightModalOpen) {
        pendingWeightEntryRef.current = null;
        onCloseDuplicateWeightModal();
      }

      const eans = currentItemToPick?.item.eans ?? [];
      if (eans.length === 0 && !isCurrentItemVariablePrice) return;

      const scannedBarcode = processBarcode(barcode, { silent: true });

      // Couldn't process the raw scanner input (empty, corrupt, etc.) — applies to
      // both WBP and standard EAN items; show the parse_failed (red) toast.
      if (!scannedBarcode) {
        showScanError("parse_failed", barcode);
        return;
      }

      // Variable price scanning: parse EAN-13 and validate wbp_id
      if (isCurrentItemVariablePrice && currentItemToPick) {
        const parsed = parseVariablePriceBarcode(scannedBarcode);

        if (!parsed) {
          showScanError("parse_failed", barcode);
          return;
        }

        if (!currentItemToPick.item.variablePriceCodes.includes(parsed.wbpId)) {
          showScanError("wrong_product", barcode);
          return;
        }

        // Match: check for duplicate weight before accepting
        scanLockedRef.current = null;
        closeIfToastActive();

        const sku = currentItemToPick.item.sku;
        if (sku) {
          const newEntry: WeightBasedPricingEntry = {
            sku,
            wbpId: parsed.wbpId,
            priceInCents: parsed.priceInCents,
            barcode: scannedBarcode,
          };
          const existingEntries =
            useVariablePriceStore.getState().variablePriceItems[currentItemToPick.item.id] ?? [];

          if (findDuplicateWeight(newEntry, existingEntries)) {
            const attemptKey = `${currentItemToPick.item.id}:${newEntry.priceInCents}`;
            const attempts = duplicateWeightAttemptsRef.current[attemptKey] ?? 0;
            duplicateWeightAttemptsRef.current[attemptKey] = attempts + 1;

            pendingWeightEntryRef.current = {
              itemId: currentItemToPick.item.id,
              entry: newEntry,
            };
            setAllowAddDuplicateWeight(attempts >= 1);
            onOpenDuplicateWeightModal();
            return;
          }

          useVariablePriceStore.getState().addVariablePriceEntry(currentItemToPick.item.id, {
            priceInCents: parsed.priceInCents,
            sku,
          });
        }
        incrementCurrentItemQuantity({ pickedByScan: true, scannedBarcode });
        return;
      }

      // Standard barcode matching
      const isInvalid = !eans.includes(scannedBarcode);

      if (isInvalid) {
        showScanError("wrong_product", barcode);
      } else {
        scanLockedRef.current = null;
        closeIfToastActive();
        incrementCurrentItemQuantity({
          pickedByScan: true,
          scannedBarcode: scannedBarcode ?? undefined,
        });
      }
    },
    [
      closeToast,
      currentItem?.sku,
      currentItemToPick,
      currentOrder?.number,
      isCurrentItemVariablePrice,
      incrementCurrentItemQuantity,
      intl,
      isDuplicateWeightModalOpen,
      isScanJETReweBagModalOpen,
      onCloseDuplicateWeightModal,
      onConfirmBarcodeIssue,
      onOpenDuplicateWeightModal,
      processBarcode,
      showScanError,
      closeIfToastActive,
    ],
  );

  const handleDuplicateWeightResolve = useCallback(
    (resolution: "ADD_WITH_SAME_WEIGHT" | "TRY_AGAIN") => {
      onCloseDuplicateWeightModal();

      if (resolution === "ADD_WITH_SAME_WEIGHT" && pendingWeightEntryRef.current) {
        const { itemId, entry } = pendingWeightEntryRef.current;
        useVariablePriceStore.getState().addVariablePriceEntry(itemId, {
          priceInCents: entry.priceInCents,
          sku: entry.sku,
        });
        incrementCurrentItemQuantity({
          scannedBarcode: entry.barcode,
        });
      }

      pendingWeightEntryRef.current = null;
    },
    [onCloseDuplicateWeightModal, incrementCurrentItemQuantity],
  );

  const onResetItemClick = useCallback(
    (itemId: string) => {
      resetItem(itemId);
    },
    [resetItem],
  );

  const handleBarCodeDebugClick = useCallback(() => {
    if (config.environment.ENVIRONMENT !== "production" && currentItemToPick?.item.eans?.[0]) {
      incrementCurrentItemQuantity({});
    }
  }, [currentItem?.id, onScan]);

  const endPickingButton = useMemo(() => {
    if (!isPickingDone || !isGiftItemPicked || !isCampaignItemPicked || !hasFlyersRemoved) {
      return null;
    }
    const shouldSkipHandoverProcess =
      ["UBER-EATS", "UBER-EATS-CARREFOUR", "WOLT"].includes(currentOrder?.reseller ?? "") ||
      isNotNullNorUndefined(currentOrder?.externalDeliveryProvider) ||
      currentOrder?.isInStore === true ||
      currentOrder?.isClickAndCollect === true;

    if (shouldSkipHandoverProcess) {
      return <EndPickingButton />;
    }
    return <GoToScanContainersButton />;
  }, [
    isPickingDone,
    isGiftItemPicked,
    isCampaignItemPicked,
    hasFlyersRemoved,
    currentOrder?.reseller,
    currentOrder?.externalDeliveryProvider,
    currentOrder?.isInStore,
    currentOrder?.isClickAndCollect,
  ]);

  const handleAskForRestocking = async (item: Item) => {
    if (item.sku) {
      try {
        await addItemToPublicRestockingList(item.sku, RestockingSource.picking);
        if (!selectedItemsForRestocking.includes(item.sku)) {
          setSelectedItemsForRestocking([...selectedItemsForRestocking, item.sku]);
        }
      } catch (e: any) {
        showToastUI({
          id: `${ASK_FOR_RESTOCKING_ERROR_TOAST_ID}_${item.sku}`,
          title: "components.picking.restocking.ask-for-restocking-error-title",
        });
      }
      sendSegmentTrackEvent("click", {
        screen_name: PageName.PICKING_PAGE,
        component_name: "ask_for_restock",
        component_value: item.sku,
        component_content: item.id,
      });
    }
  };

  const renderItemsList = (isSkippedItemsList = false) => {
    return (isSkippedItemsList ? skippedItemsSortedByShelf : itemsSortedByShelf).map(
      (itemToPick, itemIndex) => (
        <PickingListItem
          role="listitem"
          key={itemToPick.item.id}
          listItem={itemToPick}
          itemIndex={itemIndex}
          isSkipped={isSkippedItemsList}
          countryCode={countryCode}
          isAskRestockingButtonDisabled={selectedItemsForRestocking.includes(
            itemToPick.item.sku ?? "",
          )}
          elementRef={(element) => {
            (isSkippedItemsList ? skippedItemsRef : itemsRef).current[itemIndex] = element;
          }}
          onBarcodeClick={handleBarCodeDebugClick}
          onSkipItemClick={skipCurrentItem()}
          onResetItemClick={onResetItemClick}
          onItemNotFoundClick={onOpenItemNotFoundModal}
          handleAskForRestocking={() => handleAskForRestocking(itemToPick.item)}
          handleUpdateItemQuantity={handleUpdateItemQuantity}
        />
      ),
    );
  };

  useScan({
    onScan,
  });

  return (
    <Page isFull pos="relative" isBgGrey>
      {isNotNullNorUndefined(currentItemToPick) && (
        <ItemNotFoundModal
          isOpen={isItemNotFoundModalOpen}
          onClose={onCloseItemNotFoundModal}
          availableQuantity={currentItemToPick.item.availableQuantity}
          onConfirmRefund={skipCurrentItem(onCloseItemNotFoundModal)}
          pickedQuantity={currentItemToPick.pickedQuantity}
          totalQuantity={currentItemToPick.item.quantity}
        />
      )}

      <ScanJETReweBagModal
        isOpen={isScanJETReweBagModalOpen}
        onClose={handleOnCloseScanJETReweBagModal}
      />
      <DuplicateWeightModal
        isOpen={isDuplicateWeightModalOpen}
        allowAdd={allowAddDuplicateWeight}
        onResolve={handleDuplicateWeightResolve}
      />
      <PickingListHeader />
      <Box w="100%" data-testid="list-items" role="list">
        {renderItemsList()}
      </Box>
      {skippedItemsSortedByShelf.length > 0 && (
        <>
          <SeparatorHeader
            title={intl.formatMessage({
              id: "pages.picking.picking-list.skipped-items-header-title",
            })}
            hintText={intl.formatMessage({
              id: "pages.picking.picking-list.skipped-items-header-hint-text",
            })}
            ref={skippedItemsHeaderRef}
          />
          <Box w="100%" data-testid="list-items-skipped" role="list">
            {renderItemsList(true)}
          </Box>
        </>
      )}
      {isNewCustomer && (isWelcomeFlyerFlagEnabled || isCampaignProductFlagEnabled) && (
        <>
          <SeparatorHeader
            ref={giftsHeaderRef}
            title={intl.formatMessage({ id: "pages.picking.picking-list.gift-header-title" })}
          />
          {isWelcomeFlyerFlagEnabled && (
            <Box w="100%" data-testid="list-items-gift" role="list">
              <Gift />
            </Box>
          )}
          {isCampaignProductFlagEnabled && (
            <Box mt="s200" w="100%" data-testid="list-items-campaign" role="list">
              <Campaign />
            </Box>
          )}
        </>
      )}
      {isExternalProvider && (
        <>
          <SeparatorHeader
            ref={removeFlyersHeaderRef}
            title={intl.formatMessage({
              id: "pages.picking.picking-list.remove-flyers.header-title",
            })}
          />
          <Box w="100%" data-testid="list-items-remove-flyers" role="list">
            <RemoveFlyers />
          </Box>
        </>
      )}
      <Box ref={endPickingButtonRef} w="100%" py="s300" px="s200">
        {endPickingButton}
      </Box>
      {endPickingConflictData && (
        <PickingConflictModal
          isOpen
          hideProceed
          conflictData={endPickingConflictData}
          onClose={() => {
            resetPicking();
          }}
          onProceed={() => {
            resetPicking();
          }}
        />
      )}
      {shouldShowOrderCompletionWarning && (
        <OrderCompletionWarningModal onClick={confirmOrderCompletion} />
      )}
    </Page>
  );
}
